<script lang="ts" setup>
const route = useRoute()
const { parseMenuRoute, parseMenuTitle } = useNavbarParser()
const { trackEvent } = useCnzz()
const { t } = useI18n()

const props = defineProps({
  menu: {
    type: Object as () =>
      | AwesomeLayoutPageNavbarMenu
      | AwesomeLayoutPageNavbarMenuDropdownItem,
    required: true,
  },
  isDropdown: {
    type: Boolean,
    default: false,
  },
})

// 导航点击上报
const trackNavClick = () => {
  const title = parseMenuTitle(props.menu?.title)
  trackEvent('导航栏', '点击', title)
}

// 检测是否是外部链接
const isExternalLink = computed(() => {
  const menuRoute = parseMenuRoute(props.menu?.to)
  return typeof menuRoute === 'string' && /^https?:\/\//.test(menuRoute)
})

// 获取链接地址（用于外部链接）
const linkHref = computed(() => {
  const menuRoute = parseMenuRoute(props.menu?.to)
  return typeof menuRoute === 'string' ? menuRoute : ''
})

// 自定义激活状态检查，支持子路由匹配
const isMenuActive = computed(() => {
  const to = parseMenuRoute(props.menu?.to)
  if (!to) return false

  // 如果是字符串路径
  if (typeof to === 'string') {
    // 外部链接不检查激活状态
    if (/^https?:\/\//.test(to)) return false
    // 检查路径是否匹配或是否是当前路径的父级
    return route.path === to || route.path.startsWith(to + '/')
  }

  // 如果是路由对象（带 name 属性）
  if ((to as any).name) {
    const targetName = (to as any).name.toString()
    const currentName = route.name?.toString() || ''
    // 精确匹配或子路由匹配（如 news 匹配 news-id）
    return (
      currentName === targetName || currentName.startsWith(targetName + '-')
    )
  }

  return false
})

// 控制横线动画
const isHovered = ref(false)
const lineOrigin = ref('left')

const handleMouseEnter = () => {
  lineOrigin.value = 'left' // 移入时从左边开始展开
  isHovered.value = true
}

const handleMouseLeave = () => {
  lineOrigin.value = 'right' // 移出时从右边开始收起（视觉上是从左向右消失）
  isHovered.value = false
}
</script>

<template>
  <template v-if="menu?.type === 'link' && isDropdown">
    <!-- 外部链接 -->
    <a
      v-if="isExternalLink"
      :href="linkHref"
      target="_blank"
      rel="noopener noreferrer"
      class="transition-all duration-300 hover:bg-gray-100 px-4 py-2 rounded-lg text-center whitespace-nowrap text-gray-700"
      @click="trackNavClick"
    >
      {{ parseMenuTitle(menu?.title) }}
    </a>
    <!-- 内部链接 -->
    <NuxtLink v-else :to="parseMenuRoute(menu?.to)" @click="trackNavClick">
      <div
        :class="[
          'transition-all duration-300 hover:bg-gray-100 px-4 py-2 rounded-lg text-center whitespace-nowrap',
          isMenuActive ? 'text-gray-900 font-bold' : 'text-gray-700',
        ]"
      >
        {{ parseMenuTitle(menu?.title) }}
      </div>
    </NuxtLink>
  </template>
  <template v-else-if="menu?.type === 'link'">
    <!-- 外部链接 -->
    <a
      v-if="isExternalLink"
      :href="linkHref"
      target="_blank"
      rel="noopener noreferrer"
      class="relative"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @click="trackNavClick"
    >
      <span class="text-gray-700">{{ parseMenuTitle(menu?.title) }}</span>
      <div
        class="w-[58px] h-[4px] rounded-full absolute -bottom-[6px] transition-transform duration-300 ease-out"
        :class="[isHovered ? 'scale-x-100' : 'scale-x-0']"
        :style="`background: linear-gradient(135deg, #37beef 0%, #47b897 100%); left: calc(50% - 29px); transform-origin: ${lineOrigin}`"
      ></div>
    </a>
    <!-- 内部链接 -->
    <NuxtLink v-else :to="parseMenuRoute(menu?.to)" @click="trackNavClick">
      <div
        class="relative"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <span
          :class="{
            'text-gray-900 font-bold': isMenuActive,
            'text-gray-700': !isMenuActive,
          }"
          >{{ parseMenuTitle(menu?.title) }}</span
        >
        <div
          class="w-[58px] h-[4px] rounded-full absolute -bottom-[6px] transition-transform duration-300 ease-out"
          :class="[isMenuActive || isHovered ? 'scale-x-100' : 'scale-x-0']"
          :style="`background: linear-gradient(135deg, #37beef 0%, #47b897 100%); left: calc(50% - 29px); transform-origin: ${lineOrigin}`"
        ></div>
      </div>
    </NuxtLink>
  </template>
  <template v-else-if="menu?.type === 'button'">
    <AwesomeButton
      :text="parseMenuTitle(menu?.title)"
      size="xs"
      :to="parseMenuRoute(menu.to)"
    />
  </template>
</template>
